import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { parcoService } from '@/services/parco.service';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { Star, MessageSquareText, Loader2, Trash2, LogIn } from 'lucide-react';

const initials = (name = '') =>
  name.split(' ').filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join('') || 'C';

const fmtDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' }); }
  catch { return ''; }
};

/** Stelle in sola lettura. */
function Stars({ value = 0, size = 'h-4 w-4' }) {
  return (
    <span className="inline-flex items-center gap-0.5" aria-label={`${value} su 5`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} className={`${size} ${i <= Math.round(value) ? 'fill-amber-400 text-amber-400' : 'fill-none text-muted-foreground/40'}`} />
      ))}
    </span>
  );
}

/** Selettore stelle interattivo. */
function StarInput({ value, onChange }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex items-center gap-1" role="radiogroup" aria-label="Valutazione">
      {[1, 2, 3, 4, 5].map((i) => (
        <button
          key={i}
          type="button"
          onClick={() => onChange(i)}
          onMouseEnter={() => setHover(i)}
          onMouseLeave={() => setHover(0)}
          className="rounded-md p-0.5 transition-transform hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label={`${i} stelle`}
          aria-checked={value === i}
          role="radio"
          data-testid={`guide-review-star-${i}`}
        >
          <Star className={`h-7 w-7 ${i <= (hover || value) ? 'fill-amber-400 text-amber-400' : 'fill-none text-muted-foreground/40'}`} />
        </button>
      ))}
    </div>
  );
}

export function GuideReviews({ guideId }) {
  const { user, formatApiError } = useAuth();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({ average: 0, count: 0, distribution: {} });
  const [reviews, setReviews] = useState([]);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const isCustomer = user?.role === 'customer';
  const myReview = useMemo(() => reviews.find((r) => r.customer_id === user?.id), [reviews, user]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await parcoService.guideReviews(guideId);
      setSummary(res.summary || { average: 0, count: 0, distribution: {} });
      setReviews(res.reviews || []);
    } catch {
      /* silenzioso */
    } finally {
      setLoading(false);
    }
  }, [guideId]);

  useEffect(() => { load(); }, [load]);

  // prefill del form con la propria recensione esistente
  useEffect(() => {
    if (myReview) { setRating(myReview.rating); setComment(myReview.comment || ''); }
  }, [myReview]);

  const submit = async (e) => {
    e.preventDefault();
    if (rating < 1) { toast.error('Seleziona una valutazione da 1 a 5 stelle.'); return; }
    setSubmitting(true);
    try {
      await parcoService.submitGuideReview(guideId, { rating, comment });
      toast.success(myReview ? 'Recensione aggiornata' : 'Grazie per la tua recensione!');
      await load();
    } catch (err) {
      toast.error(formatApiError?.(err.response?.data?.detail) || 'Invio non riuscito');
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async () => {
    setSubmitting(true);
    try {
      await parcoService.deleteGuideReview(guideId);
      setRating(0); setComment('');
      toast.success('Recensione eliminata');
      await load();
    } catch (err) {
      toast.error(formatApiError?.(err.response?.data?.detail) || 'Eliminazione non riuscita');
    } finally {
      setSubmitting(false);
    }
  };

  const dist = summary.distribution || {};
  const total = summary.count || 0;

  return (
    <section className="sila-section border-t border-border py-10 sm:py-14" data-testid="guide-reviews">
      <div className="mb-6 flex items-center gap-2">
        <MessageSquareText className="h-5 w-5 text-primary" />
        <h2 className="font-display text-xl font-semibold text-foreground">Recensioni</h2>
      </div>

      {loading ? (
        <div className="grid gap-6 lg:grid-cols-3">
          <Skeleton className="h-40 w-full rounded-xl" />
          <Skeleton className="h-40 w-full rounded-xl lg:col-span-2" />
        </div>
      ) : (
        <div className="grid gap-8 lg:grid-cols-3">
          {/* Riepilogo */}
          <div className="lg:col-span-1">
            <Card className="p-5" data-testid="guide-reviews-summary">
              <div className="flex items-end gap-3">
                <span className="font-display text-4xl font-bold text-foreground" data-testid="guide-reviews-average">
                  {total ? summary.average.toFixed(1) : '—'}
                </span>
                <div className="pb-1">
                  <Stars value={summary.average} />
                  <p className="mt-1 text-xs text-muted-foreground" data-testid="guide-reviews-count">
                    {total === 0 ? 'Ancora nessuna recensione' : `${total} ${total === 1 ? 'recensione' : 'recensioni'}`}
                  </p>
                </div>
              </div>
              {total > 0 && (
                <div className="mt-4 space-y-1.5">
                  {[5, 4, 3, 2, 1].map((star) => {
                    const c = dist[String(star)] || 0;
                    const pct = total ? Math.round((c / total) * 100) : 0;
                    return (
                      <div key={star} className="flex items-center gap-2 text-xs">
                        <span className="w-3 text-muted-foreground">{star}</span>
                        <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                        <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
                          <div className="h-full rounded-full bg-amber-400" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="w-6 text-right text-muted-foreground">{c}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>

            {/* Form recensione */}
            {isCustomer ? (
              <Card className="mt-4 p-5" data-testid="guide-review-form">
                <h3 className="font-display text-base font-semibold text-foreground">
                  {myReview ? 'Modifica la tua recensione' : 'Lascia una recensione'}
                </h3>
                <form onSubmit={submit} className="mt-3 space-y-3">
                  <StarInput value={rating} onChange={setRating} />
                  <Textarea
                    rows={3}
                    value={comment}
                    onChange={(e) => setComment(e.target.value.slice(0, 1000))}
                    placeholder="Racconta la tua esperienza con questa guida…"
                    className="resize-none"
                    data-testid="guide-review-comment"
                  />
                  <div className="flex items-center gap-2">
                    <Button type="submit" disabled={submitting} data-testid="guide-review-submit">
                      {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      {myReview ? 'Aggiorna' : 'Pubblica'}
                    </Button>
                    {myReview && (
                      <Button type="button" variant="ghost" className="text-destructive hover:text-destructive" onClick={remove} disabled={submitting} data-testid="guide-review-delete">
                        <Trash2 className="mr-1.5 h-4 w-4" />Elimina
                      </Button>
                    )}
                  </div>
                </form>
              </Card>
            ) : !user ? (
              <Card className="mt-4 flex flex-col items-start gap-2 p-5" data-testid="guide-review-login-prompt">
                <p className="text-sm text-muted-foreground">Accedi come cliente per lasciare la tua recensione.</p>
                <Button asChild variant="outline" size="sm">
                  <Link to={`/cliente/accedi?next=${encodeURIComponent(location.pathname)}`}>
                    <LogIn className="mr-2 h-4 w-4" />Accedi
                  </Link>
                </Button>
              </Card>
            ) : null}
          </div>

          {/* Lista recensioni */}
          <div className="lg:col-span-2">
            {reviews.length === 0 ? (
              <Card className="flex flex-col items-center justify-center gap-2 p-12 text-center text-muted-foreground" data-testid="guide-reviews-empty">
                <MessageSquareText className="h-8 w-8" />
                <p>Nessuna recensione. Sii il primo a raccontare la tua esperienza!</p>
              </Card>
            ) : (
              <ul className="space-y-4" data-testid="guide-reviews-list">
                {reviews.map((r) => (
                  <li key={r.id} data-testid={`guide-review-${r.id}`}>
                    <Card className="p-4">
                      <div className="flex items-start gap-3">
                        <Avatar className="h-10 w-10">
                          {r.customer_photo ? <AvatarImage src={r.customer_photo} alt={r.customer_name} /> : null}
                          <AvatarFallback>{initials(r.customer_name)}</AvatarFallback>
                        </Avatar>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-medium text-foreground">{r.customer_name}</span>
                            {r.customer_id === user?.id && (
                              <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">La tua recensione</span>
                            )}
                          </div>
                          <div className="mt-0.5 flex items-center gap-2">
                            <Stars value={r.rating} size="h-3.5 w-3.5" />
                            <span className="text-xs text-muted-foreground">{fmtDate(r.updated_at || r.created_at)}</span>
                          </div>
                          {r.comment ? <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-foreground/90">{r.comment}</p> : null}
                        </div>
                      </div>
                    </Card>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

export default GuideReviews;
